import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const JobBoard = () => {
  const [selectedFilters, setSelectedFilters] = useState({
    department: 'all',
    level: 'all',
    location: 'all'
  });

  const jobOpenings = [
    {
      id: 1,
      title: "Senior Management Consultant",
      department: "Consulting",
      level: "Senior",
      location: "New York, NY",
      type: "Full-time",
      posted: "2 days ago",
      description: "Lead strategic transformation initiatives for Fortune 500 clients across multiple industries.",
      requirements: [
        "5+ years management consulting experience",
        "MBA from top-tier business school preferred",
        "Strong analytical and problem-solving skills",
        "Excellent client presentation abilities"
      ],
      benefits: ["$120k-160k", "Equity", "Health", "401k"],
      urgent: true
    },
    {
      id: 2,
      title: "Technical Solutions Architect",
      department: "Technology",
      level: "Senior",
      location: "San Francisco, CA",
      type: "Full-time",
      posted: "1 week ago",
      description: "Design and implement enterprise-scale digital transformation solutions for our clients.",
      requirements: [
        "7+ years enterprise architecture experience",
        "Cloud platforms expertise (AWS, Azure, GCP)",
        "Strong programming background",
        "Experience with microservices and APIs"
      ],
      benefits: ["$130k-170k", "Equity", "Health", "Remote"],
      urgent: false
    },
    {
      id: 3,
      title: "Junior Business Analyst",
      department: "Consulting",
      level: "Entry",
      location: "Chicago, IL",
      type: "Full-time",
      posted: "3 days ago",
      description: "Support consulting teams in delivering data-driven insights and recommendations to clients.",
      requirements: [
        "Bachelor's degree in Business, Economics, or related field",
        "Strong analytical and quantitative skills",
        "Proficiency in Excel, PowerPoint, and SQL",
        "Excellent communication and teamwork abilities"
      ],
      benefits: ["$65k-85k", "Health", "401k", "Learning"],
      urgent: false
    },
    {
      id: 4,
      title: "Operations Excellence Manager",
      department: "Operations",
      level: "Mid",
      location: "Austin, TX",
      type: "Full-time",
      posted: "5 days ago",
      description: "Drive process improvement initiatives and operational efficiency across client organizations.",
      requirements: [
        "3-5 years operations or process improvement experience",
        "Lean Six Sigma certification preferred",
        "Experience with change management",
        "Strong project management skills"
      ],
      benefits: ["$95k-125k", "Health", "401k", "Flexible"],
      urgent: false
    },
    {
      id: 5,
      title: "Data Science Consultant",
      department: "Technology",
      level: "Mid",
      location: "Boston, MA",
      type: "Full-time",
      posted: "1 week ago",
      description: "Develop advanced analytics solutions and machine learning models for client business challenges.",
      requirements: [
        "3+ years data science experience",
        "Strong Python/R programming skills",
        "Experience with ML frameworks and cloud platforms",
        "Business acumen and client-facing skills"
      ],
      benefits: ["$105k-140k", "Equity", "Health", "Remote"],
      urgent: true
    },
    {
      id: 6,
      title: "Principal Consultant - Healthcare",
      department: "Consulting",
      level: "Principal",
      location: "Remote",
      type: "Full-time",
      posted: "4 days ago",
      description: "Lead healthcare transformation engagements and build our healthcare practice capabilities.",
      requirements: [
        "10+ years healthcare consulting experience",
        "Deep knowledge of healthcare regulations and trends",
        "Proven track record of business development",
        "Advanced degree preferred"
      ],
      benefits: ["$180k-250k", "Equity", "Health", "Remote"],
      urgent: false
    }
  ];

  const filterOptions = {
    department: ['all', 'Consulting', 'Technology', 'Operations'],
    level: ['all', 'Entry', 'Mid', 'Senior', 'Principal'],
    location: ['all', 'Indore', 'Madhya Pradesh', 'India']
  };

  const filteredJobs = jobOpenings.filter(job => {
    return (
      (selectedFilters.department === 'all' || job.department === selectedFilters.department) &&
      (selectedFilters.level === 'all' || job.level === selectedFilters.level) &&
      (selectedFilters.location === 'all' || job.location === selectedFilters.location)
    );
  });

  const handleFilterChange = (filterType, value) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const getBenefitIcon = (benefit) => {
    if (benefit.includes('k')) return 'DollarSign';
    if (benefit === 'Equity') return 'TrendingUp';
    if (benefit === 'Health') return 'Heart';
    if (benefit === '401k') return 'PiggyBank';
    if (benefit === 'Remote') return 'Home';
    if (benefit === 'Flexible') return 'Clock';
    if (benefit === 'Learning') return 'BookOpen';
    return 'Star';
  };

  return (
    <section className="py-20 bg-muted text-gray-800 dark:bg-gray-900 dark:text-gray-200"> {/* Set base text color for the section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          {/* Assuming 'primary' is a distinct color that contrasts well with muted/dark-background */}
          <h2 className="text-3xl lg:text-4xl font-bold text-primary dark:text-brand-light-blue font-heading mb-6">
            Current Opportunities
          </h2>
          <p className="text-xl text-text-secondary font-body max-w-3xl mx-auto">
            Join our growing team and make an impact from day one. We're looking for passionate
            professionals who want to shape the future of business transformation.
          </p>
        </div>

        {/* Filters */}
        {/* Adjusted bg-card/80 and text colors for better contrast on a potentially varying background */}
        <div className="glass-morph bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border border-white/20 dark:border-gray-700 rounded-xl p-6 mb-8 shadow-sm">
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(filterOptions).map(([filterType, options]) => (
              <div key={filterType}>
                {/* Changed text-white to text-gray-700/dark:text-gray-300 for contrast */}
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 capitalize">
                  {filterType}
                </label>
                <select
                  value={selectedFilters[filterType]}
                  onChange={(e) => handleFilterChange(filterType, e.target.value)}
                  // Adjusted select colors for contrast
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                             focus:ring-2 focus:ring-primary dark:focus:ring-brand-light-blue 
                             focus:border-transparent text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-700"
                >
                  {options.map(option => (
                    <option key={option} value={option}>
                      {option === 'all' ? `All ${filterType}s` : option}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between">
            {/* Changed text-white/70 to text-gray-600/dark:text-gray-400 for better contrast */}
            <span className="text-sm text-text-secondary">
              Showing {filteredJobs.length} of {jobOpenings.length} positions
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedFilters({ department: 'all', level: 'all', location: 'all' })}
              // Ensure outline button has clear text color on various backgrounds
              className="border-primary text-primary hover:bg-primary hover:text-white 
                         dark:border-brand-light-blue dark:text-brand-light-blue dark:hover:bg-brand-light-blue dark:hover:text-gray-900"
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Job Listings */}
        <div className="space-y-6">
          {filteredJobs.map(job => (
            // Adjusted bg-card/80 and text colors for better contrast
            <div key={job.id} className="glass-morph bg-white/80 dark:bg-gray-800/80 backdrop-blur-md 
                                        border border-white/20 dark:border-gray-700 rounded-xl p-6 
                                        shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        {/* Assuming 'primary' is a dark color, it's fine on white/80. 
                            Added dark mode alternative. */}
                        <h3 className="text-xl font-bold text-primary dark:text-brand-light-blue font-heading">
                          {job.title}
                        </h3>
                        {job.urgent && (
                          <span className="bg-accent text-white text-xs px-2 py-1 rounded-full font-semibold">
                            Urgent {/* Text white on accent is generally good */}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-text-secondary"> {/* WCAG AA compliant */}
                        <div className="flex items-center gap-1">
                          <Icon name="Building" size={16} />
                          <span>{job.department}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon name="MapPin" size={16} />
                          <span>{job.location}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon name="Clock" size={16} />
                          <span>{job.type}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon name="Calendar" size={16} />
                          <span>Posted {job.posted}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Changed text-white/80 to text-gray-700/dark:text-gray-300 */}
                  <p className="text-gray-700 dark:text-gray-300 font-body mb-4 line-clamp-2">
                    {job.description}
                  </p>

                  {/* Benefits */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {job.benefits.map((benefit, index) => (
                      <div key={index} className="flex items-center gap-1 bg-gray-200 dark:bg-gray-700 px-3 py-1 rounded-full text-sm"> {/* Changed bg-muted to bg-gray-200/dark:bg-gray-700 */}
                        <Icon name={getBenefitIcon(benefit)} size={14} className="text-accent dark:text-brand-light-blue" /> {/* Accent or light-blue for dark mode */}
                        <span className="text-gray-800 dark:text-gray-200">{benefit}</span> {/* Changed text-white to text-gray-800/dark:text-gray-200 */}
                      </div>
                    ))}
                  </div>

                  {/* Key Requirements Preview */}
                  <div className="mb-4">
                    {/* Changed text-white to text-gray-700/dark:text-gray-300 */}
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Key Requirements:</h4>
                    {/* Changed text-white/80 to text-gray-700/dark:text-gray-300 */}
                    <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                      {job.requirements.slice(0, 2).map((req, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <Icon name="Check" size={12} className="text-accent dark:text-brand-light-blue mt-1 flex-shrink-0" />
                          <span>{req}</span>
                        </li>
                      ))}
                      {job.requirements.length > 2 && (
                        <li className="text-accent dark:text-brand-light-blue text-sm"> {/* Accent or light-blue for dark mode */}
                          +{job.requirements.length - 2} more requirements
                        </li>
                      )}
                    </ul>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:w-48">
                  <Button
                    variant="default"
                    // Assuming primary is dark, text-white is good on it. Added dark mode.
                    className="bg-primary hover:bg-primary/90 text-white dark:bg-brand-light-blue dark:hover:bg-brand-light-blue/90 dark:text-gray-900"
                    fullWidth
                  >
                    Apply Now
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    // White text on transparent/glass-morph background, with dark mode for consistency.
                    className="border-white/50 text-white hover:bg-white/10 hover:border-white/80
                               dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:border-gray-500"
                  >
                    Learn More
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredJobs.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="text-text-secondary mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No positions found</h3>
            <p className="text-text-secondary mb-4">
              Try adjusting your filters or check back later for new opportunities.
            </p>
            <Button
              variant="outline"
              onClick={() => setSelectedFilters({ department: 'all', level: 'all', location: 'all' })}
              // Ensure outline button has clear text color on various backgrounds
              className="border-primary text-primary hover:bg-primary hover:text-white 
                         dark:border-brand-light-blue dark:text-brand-light-blue dark:hover:bg-brand-light-blue dark:hover:text-gray-900"
            >
              Clear All Filters
            </Button>
          </div>
        )}

        {/* Job Alerts CTA */}
        {/* This section's colors seemed mostly fine, but added specific dark mode classes for consistency */}
        <div className="mt-16 bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold font-heading mb-4">
            Don't See the Perfect Role?
          </h3>
          <p className="text-lg text-primary font-body mb-6 max-w-2xl mx-auto">
            Set up job alerts and be the first to know when new opportunities that match
            your interests become available.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              variant="default"
              // Adjusted brand-cyan for consistency in light/dark context
              className="glass-morph bg-brand-cyan/30 text-white border border-brand-cyan/50 hover:bg-brand-cyan/50
                         dark:bg-brand-cyan/50 dark:hover:bg-brand-cyan/70 dark:border-brand-cyan/70"
            >
              Create Job Alert
            </Button>
            <Button
              variant="outline"
              className="glass-morph border-white/20 text-white hover:bg-white/10 hover:border-white/40
                         dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:border-gray-600"
            >
              Submit General Application
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JobBoard;